import {
  REPORT_TEMPLATES,
  cloneTemplateEntities,
  type ReportTemplate,
} from './reportTemplates';
import { splitTemplate, type TemplateParts } from './templateParts';
import type { ReportEntity } from './types';

export interface CopilotGenerationResult {
  parts: TemplateParts;
  entities: ReportEntity[];
  suggestedName: string;
  template: ReportTemplate;
  summary: string;
}

const PROMPT_HINTS: { keywords: string[]; templateId: string }[] = [
  { keywords: ['conversation', 'chat', 'intent', 'session', 'bot'], templateId: 'chatbot-dashboard' },
  { keywords: ['executive', 'kpi', 'summary', 'leadership'], templateId: 'executive-dashboard' },
  { keywords: ['sales', 'revenue', 'pipeline', 'deal'], templateId: 'sales-pipeline' },
  { keywords: ['user', 'engagement', 'retention', 'active'], templateId: 'user-engagement' },
  { keywords: ['performance', 'latency', 'token', 'cost'], templateId: 'intent-performance' },
  { keywords: ['compliance', 'audit', 'policy'], templateId: 'compliance-audit' },
  { keywords: ['finance', 'budget', 'forecast'], templateId: 'financial-summary' },
];

function scoreTemplate(prompt: string, template: ReportTemplate): number {
  const lower = prompt.toLowerCase();
  let score = 0;
  if (lower.includes(template.category.toLowerCase())) score += 3;
  if (lower.includes(template.name.toLowerCase())) score += 4;
  for (const word of template.description.toLowerCase().split(/\W+/)) {
    if (word.length > 3 && lower.includes(word)) score += 1;
  }
  const hint = PROMPT_HINTS.find((h) => h.templateId === template.id);
  if (hint?.keywords.some((k) => lower.includes(k))) score += 5;
  return score;
}

export function pickTemplateForPrompt(prompt: string): ReportTemplate {
  const hint = PROMPT_HINTS.find((h) => h.keywords.some((k) => prompt.toLowerCase().includes(k)));
  if (hint) {
    const matched = REPORT_TEMPLATES.find((t) => t.id === hint.templateId);
    if (matched) return matched;
  }

  const ranked = [...REPORT_TEMPLATES]
    .map((t) => ({ t, score: scoreTemplate(prompt, t) }))
    .sort((a, b) => b.score - a.score);

  return ranked[0]?.score ? ranked[0].t : REPORT_TEMPLATES[0];
}

export function generateFromPrompt(prompt: string): CopilotGenerationResult {
  const template = pickTemplateForPrompt(prompt);
  const parts = splitTemplate(template.htmlTemplate);
  const entities = cloneTemplateEntities(template);

  const summary = `I prepared a **${template.name}** layout with ${entities.length} data entities bound for \`{{entity.field}}\` placeholders. Review it in Source or Preview, then tweak HTML/CSS as needed.`;

  return {
    parts,
    entities,
    suggestedName: template.suggestedName,
    template,
    summary,
  };
}

export const COPILOT_STARTER_PROMPTS = [
  'Build a conversation metrics dashboard for my chatbot',
  'Create an executive KPI summary with trend highlights',
  'Generate a sales pipeline performance report',
  'Show user engagement and retention analytics',
];
